import React, { useState, useEffect } from "react"
import { FormButton } from "../members/formComponents/FormButton"
import { Container, Form, Alert } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import { selectVslaDebtBalance } from "../../store/reducers/getVslaBalance"
import { useSelector, useDispatch } from "react-redux"
import { selectMemberSharesFund } from "../../store/reducers/getMemberBalance"
import { toast } from "react-toastify"
import {
  getCashoutDetails,
  confirmMidCycleCashout,
} from "../../store/reducers/cashout/actions"
import { t } from "@lingui/macro"
import { NavLink } from "react-router-dom"
import { setCurrentPage } from "../../store/reducers/navigation"
import StellarLoader from "../layout/StellarLoader"

const successStyle = {
  fontFamily: "quicksand",
  fontSize: "20px",
  color: "green",
  fontWeight: "bolder",
}

const SuccessMessage = () => {
  return (
    <div style={successStyle}>
      <Trans id="notifications.cash_out_success_msg" />
    </div>
  )
}

const MidCycleCashOut = () => {
  const { i18n } = useLingui()
  const [cashOutType] = useState("midCycle")
  const [loading, setLoading] = useState(false)
  const [loadingCashoutData, setLoadingCashoutData] = useState(false)
  const [cashoutBreakDown, setCashoutBreakDown] = useState([])
  const [noCashBreakdownDetails, setNoCashBreakdownDetails] = useState(false)
  const [hasLoan, setHasLoan] = useState(false)
  const [hideCashoutTypes, setHideCashoutTypes] = useState("")

  const dispatch = useDispatch()
  const { memberDebt, groupDebt, meeting, member, vsla, group, transactions } =
    useSelector((state) => ({
      memberDebt: (id) => state.account.members[id]?.loan,
      groupDebt: selectVslaDebtBalance(state),
      membersSaving: selectMemberSharesFund(state),
      meeting: state.currentOngoingMeeting,
      member: state.loadedMember,
      vsla: state.vsla,
      group: state.group,
      transactions: state.meetingTransactions,
    }))

  const notify = (msg) => toast(msg)

  const cashedOut = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return false
    }
    return arr.every((item) => item.amount === 0)
  }

  const getDetails = async () => {
    setLoadingCashoutData(true)
    setCashoutBreakDown([])
    const res = await dispatch(getCashoutDetails(member, true))
    if (!cashedOut(res?.balances || [])) {
      setLoadingCashoutData(false)
      setCashoutBreakDown(res?.balances || [])
    } else {
      setNoCashBreakdownDetails(true)
      setLoadingCashoutData(false)
    }
  }

  const handleCashout = async () => {
    let memLoan = memberDebt(member?.pubkey)?.balance
    if (memLoan > 0 && groupDebt.amount > 0) setHasLoan(true)
    else {
      await getDetails()
    }
  }

  useEffect(() => {
    handleCashout()
  }, [])

  const processCashOut = () => {
    const lang_code = i18n.locale
    let savingsCashOutValue = cashoutBreakDown.filter(
      ({ fundName }) => fundName === "savings",
    )
    savingsCashOutValue = savingsCashOutValue.length
      ? savingsCashOutValue[0].amount
      : 0

    const lang = {
      fr: {
        message: `Confirmer le retrait de ${savingsCashOutValue} pour le membre`,
      },
      en: {
        message: `Confirm cash out of ${savingsCashOutValue} for member`,
      },
      sw: {
        message: `Thibitisha mwanachama atapewa mchango wa shilingi ${savingsCashOutValue}`,
      },
      es: {
        message: `Confirmar efectivo de ${savingsCashOutValue} lempiras para miembro`,
      },
      pt: {
        message: `Confirme o saque de ${savingsCashOutValue} para o membro`,
      },
      ar: {
        message: `أكد سحب ${savingsCashOutValue} للأسرة`,
      },
    }

    if (window.confirm(lang[lang_code].message)) {
      setLoading(true)
      setHideCashoutTypes("none")

      dispatch(confirmMidCycleCashout(member.id))

      notify(<SuccessMessage />)

      setLoading(false)

      dispatch(setCurrentPage("group-memberList"))
    }
  }

  const onSubmit = (e) => {
    e.preventDefault()
    processCashOut()
  }

  const eligibleForCashout = () => {
    if (cashoutBreakDown.length) {
      return cashOutType
        ? cashOutType === "midCycle"
          ? memberDebt(member?.pubkey)?.balance < 1
          : groupDebt.amount < 1
        : false
    }
    return false
  }

  const deactivateSubmit = () => {
    if (!eligibleForCashout()) {
      return true
    } else if (loadingCashoutData) {
      return true
    }
    return false
  }

  const headerStyle = {
    color: "#E36F1E",
    fontFamily: "quicksand",
    fontWeight: "bolder",
    fontSize: "1.2rem",
    textAlign: "center",
    paddingBottom: "0.5em",
  }

  if (loading) {
    return <StellarLoader />
  }

  return (
    <Container>
      <div>
        <h4 style={headerStyle}>{member?.full_name}</h4>
      </div>
      <h4 style={headerStyle}>
        <Trans id="menu_mem.midCycle_Cashout" />
      </h4>

      <Form onSubmit={onSubmit}>
        {noCashBreakdownDetails && (
          <Alert color="danger">
            <h4>
              <Trans id="alert.error" />
            </h4>
            <span>
              <Trans id="alert.member_cashed_out" />
            </span>
          </Alert>
        )}
        {hasLoan && (
          <Alert color="danger">
            <h4>
              <Trans id="alert.failed" />
            </h4>
            <span>
              <Trans id="alert.member_pending_loan" />
            </span>
          </Alert>
        )}
        {loadingCashoutData && (
          <span style={{ display: "flex" }}>
            <span
              className="dots-loader"
              style={{
                color: "#E36F1E",
                textAlign: "center",
                fontSize: 12,
                margin: "auto",
              }}
            >
              processing<span style={{ position: "absolute" }}></span>
            </span>
          </span>
        )}
        <ul style={{ listStyle: "none" }}>
          {cashoutBreakDown.map((item) => (
            <li key={item.fundName} style={{ padding: "4px 0" }}>
              {`${item.fundName}: ${parseFloat(item.amount).toFixed(2)}`}{" "}
            </li>
          ))}
        </ul>

        <br />
        <FormButton
          type={"submit"}
          value="Submit"
          name="submit"
          className="btn btn-lg form-control"
          disabled={deactivateSubmit()}
        />
      </Form>
      <Alert color="info" style={{ display: hideCashoutTypes }}>
        <p>
          <Trans id="cashOut.settings_summary_title" />
        </p>
        <p>
          <strong>
            <Trans id="cashOut.savings" />
          </strong>
          : {group?.metadata?.savingsCashOutType}
        </p>
        <p>
          <strong>
            <Trans id="cashOut.loan_interest" />
          </strong>
          : {group?.metadata?.loanInterestCashOutType}
        </p>
        <p>
          <strong>
            <Trans id="cashOut.membership" />
          </strong>
          : {group?.metadata?.membershipCashOutType}
        </p>
        <p>
          <strong>
            <Trans id="cashOut.welfare" />
          </strong>
          : {group?.metadata?.welfareCashOutType}
        </p>
        <p>
          <strong>
            <Trans id="cashOut.fine" />
          </strong>
          : {group?.metadata?.fineCashOutType}
        </p>
        <br />
        <p>
          <Trans id="cashOut.call_to_action" />{" "}
          <NavLink
            onClick={(e) => {
              const hasTransaction =
                transactions.filter(
                  ({ meetingId }) => parseInt(meetingId) === parseInt(meeting[0].id),
                ).length > 0
              if (hasTransaction) {
                e.preventDefault()
                alert(
                  t({
                    id: "You cannot edit settings while there are pending transactions",
                  }),
                )
              }
            }}
            to={{
              pathname: `/Groups/${group?.id}/settings`,
              state: {
                description: group?.name,
                vsla_id: group?.id,
                group_id: vsla?.id,
              },
            }}
          >
            <Trans id="cashOut.write_here" />
          </NavLink>
        </p>
      </Alert>
    </Container>
  )
}

MidCycleCashOut.propTypes = {}

export default MidCycleCashOut
